import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Loader2, Link as LinkIcon } from "lucide-react";

export default function InviteDialog({ open, onOpenChange, player }) {
  const [link, setLink] = useState("");
  const [claimed, setClaimed] = useState(false);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setLink("");
    try {
      const res = await api.post(`/players/${player.id}/invite`);
      setLink(`${window.location.origin}${res.data.invite_path}`);
      setClaimed(res.data.already_claimed);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && player) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            Invite {player?.name}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Share this link so the player can set a password and manage their own profile. Training and match data stay coach-only.
          </DialogDescription>
        </DialogHeader>

        {claimed && (
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#FF9500]">
            Heads up — this player has already claimed a profile. A new link only works if you remove their account first.
          </p>
        )}

        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center gap-2 flex-1 bg-[#0A0A0A] border border-[#27272A] px-3 h-11 overflow-hidden">
            <LinkIcon className="w-4 h-4 text-[#71717A] shrink-0" />
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#71717A]" />
            ) : (
              <Input
                data-testid="invite-link-input"
                readOnly
                value={link}
                className="border-0 bg-transparent p-0 h-auto text-sm text-white focus-visible:ring-0"
              />
            )}
          </div>
          <Button data-testid="copy-invite-link" onClick={copy} disabled={!link} className="rounded-none bg-[#15803D] hover:bg-[#166534] h-11 shrink-0">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[11px] text-[#71717A] mt-1">Link expires in 7 days.</p>
      </DialogContent>
    </Dialog>
  );
}
